import React, { useState, useEffect, useRef } from "react";
import { UserCircleIcon, ChevronDownIcon } from "@heroicons/react/24/outline";
import { useAuth } from "../Authentication/AuthContext";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { FiTag, FiUsers, FiGift } from "react-icons/fi";
import { useGetVendorKYCStatusQuery } from "../../redux/service/vendorService";
import LogoutConfirmationModal from "../Authentication/LogoutConfirmationModal";
import { useDispatch, useSelector } from "react-redux";
import { setVendorIsApproved } from "../../redux/slice/upSwapSlice";
import { FaStoreAlt } from "react-icons/fa";
import { BookOpenIcon } from "lucide-react";
import NotificationBell from "../Notification/NotificationBell";
import MobileUserHeader from "./MobileUserHeader";
import LocationSelector from "../Common/LocationSelector";
import { useIsMobile } from "../Common/ReusableFunction";

const navItems = [
  {
    name: "Activities",
    icon: FiUsers,
    dropdownItems: [
      { name: "Browse Activities", path: "/LocalActivities" },
      { name: "Create Activity", path: "/PostActivities" },
      { name: "My Created Activities", path: "/MyActivities" },
    ],
  },
  {
    name: "Services",
    icon: FiGift,
    dropdownItems: [
      { name: "Browse Services", path: "/services/browse" },
      { name: "Favorite Services", path: "/services/favorite" },
    ],
  },
  {
    name: "Deals",
    icon: FiTag,
    dropdownItems: [
      { name: "Browse Deals", path: "/PopularDeals" },
      { name: "My Orders", path: "/mypurchasedeal" },
    ],
  },
  {
    name: "Vendors",
    icon: FaStoreAlt,
    dropdownItems: [
      { name: "Browse Vendors", path: "/VendorList" },
      { name: "Favorite Vendors", path: "/Favorite" },
    ],
  },
  {
    name: "Bookings",
    icon: BookOpenIcon,
    dropdownItems: [{ name: "My Bookings", path: "/customer/bookings" }],
  },
];

const UserHeader = () => {
  const isMobile = useIsMobile();
  const { userRole, setUserRole, userData: loginUserData } = useAuth();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [isUserDropdownOpen, setIsUserDropdownOpen] = useState(false);
  const [openNavDropdown, setOpenNavDropdown] = useState(null);
  const [showLogoutConfirmModal, setShowLogoutConfirmModal] = useState(false);

  const userDropdownRef = useRef(null);
  const navDropdownRefs = useRef([]);

  const [currentLocation, setCurrentLocation] = useState(() => {
    const storedLocation = localStorage.getItem("userLocation");
    return storedLocation
      ? JSON.parse(storedLocation)
      : {
          city: "",
          state: "",
          latitude: null,
          longitude: null,
          country: "",
          area: "",
          formattedAddress: "",
        };
  });

  const vendorId = localStorage.getItem("vendor_id");
  const { vendorIsApproved } = useSelector((state) => state.upSwap);

  const handleLogoutClick = (e) => {
    e.stopPropagation();
    setIsUserDropdownOpen(false);
    setShowLogoutConfirmModal(true);
  };

  const handleUserDropdownToggle = () => {
    setIsUserDropdownOpen(!isUserDropdownOpen);
    setOpenNavDropdown(null);
  };

  const handleNavDropdownToggle = (index) => {
    setOpenNavDropdown(openNavDropdown === index ? null : index);
    setIsUserDropdownOpen(false);
  };

  const handleDropdownItemClick = (item) => {
    if (item.path) {
      navigate(item.path);
      setOpenNavDropdown(null);
    }
  };

  const handleBecomeAVendorClick = (e) => {
    e.stopPropagation();
    const phoneNumber = localStorage.getItem("phone_number");
    const email = localStorage.getItem("email");

    if (!phoneNumber || !email) {
      toast.info(
        "Please update your user profile first, and then complete KYC."
      );
      setIsUserDropdownOpen(false);
      return;
    }

    navigate("/BasicInfo");
    setIsUserDropdownOpen(false);
  };

  const {
    data: kycData,
    isLoading: kycDataIsLoading,
    isFetching: kycDataIsFetching,
  } = useGetVendorKYCStatusQuery(vendorId, { skip: !vendorId });

  useEffect(() => {
    if (!kycDataIsFetching && !kycDataIsLoading) {
      dispatch(setVendorIsApproved(kycData?.is_approved));
    }
  }, [kycDataIsLoading, kycDataIsFetching, kycData, dispatch]);

  useEffect(() => {
    navDropdownRefs.current = navItems.map(
      (_, i) => navDropdownRefs.current[i] ?? React.createRef()
    );
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      // User dropdown
      if (
        userDropdownRef.current &&
        !userDropdownRef.current.contains(event.target)
      ) {
        setIsUserDropdownOpen(false);
      }

      // Nav dropdowns
      let clickedInsideAnyNav = navDropdownRefs.current.some(
        (ref) => ref.current && ref.current.contains(event.target)
      );
      if (!clickedInsideAnyNav) {
        setOpenNavDropdown(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  if (isMobile) {
    return (
      <>
        <MobileUserHeader
          navItems={navItems}
          loginUserData={loginUserData}
          vendorIsApproved={vendorIsApproved}
          userRole={userRole}
          setUserRole={setUserRole}
          handleLogoutClick={handleLogoutClick}
          handleBecomeAVendorClick={handleBecomeAVendorClick}
          setCurrentLocation={setCurrentLocation}
          currentLocation={currentLocation}
        />
        <LogoutConfirmationModal
          isOpen={showLogoutConfirmModal}
          onClose={() => {
            setShowLogoutConfirmModal(false);
          }}
        />
      </>
    );
  }

  return (
    <header className="bg-white shadow-sm py-4 px-6 border-b border-gray-200">
      <div className="container mx-auto flex items-center justify-between">
        <div className="flex-shrink-0 cursor-pointer">
          <img
            src="/upswap.png"
            alt="UpSway Logo"
            className="h-10"
            onClick={() => navigate("/LocalActivities")}
          />
        </div>

        <LocationSelector
          initialLocation={currentLocation}
          onLocationSelected={(newLocation) => {
            setCurrentLocation(newLocation);
            localStorage.setItem("userLocation", JSON.stringify(newLocation));
          }}
          displayClassName="ml-4"
        />

        <LogoutConfirmationModal
          isOpen={showLogoutConfirmModal}
          onClose={() => {
            setShowLogoutConfirmModal(false);
          }}
        />

        <nav className="hidden lg:flex flex-grow justify-center space-x-8">
          {navItems.map((item, index) => (
            <div
              key={index}
              ref={navDropdownRefs.current[index]}
              className="relative text-gray-700 hover:text-black font-semibold cursor-pointer flex items-center text-sm nav-dropdown-container"
              onClick={() => handleNavDropdownToggle(index)}
            >
              {item.icon && (
                <item.icon className="h-4 w-4 mr-1 text-gray-600 group-hover:text-black" />
              )}
              <span>{item.name}</span>
              <ChevronDownIcon className="h-4 w-4 ml-1 text-gray-400" />
              <div
                className={`absolute top-full left-1/2 -translate-x-1/2 mt-2 w-44 bg-white border border-gray-200 rounded-md shadow-lg z-50 ${
                  openNavDropdown === index ? "flex flex-col gap-2" : "hidden"
                }`}
              >
                {item.dropdownItems.map((dItem) => (
                  <Link
                    key={dItem.name}
                    to={dItem.path || "#"}
                    className={`block px-4 py-2 text-sm relative ${
                      dItem.commingsoon
                        ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                        : "text-gray-700 hover:bg-gray-100"
                    }`}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (dItem.commingsoon) return;
                      handleDropdownItemClick(dItem);
                    }}
                  >
                    {dItem.commingsoon && (
                      <span className="absolute top-1 left-4 text-xs text-orange-500 font-semibold">
                        Coming Soon
                      </span>
                    )}
                    <span className={dItem.commingsoon ? "pt-2 block" : ""}>
                      {dItem.name}
                    </span>
                  </Link>
                ))}
              </div>
            </div>
          ))}
        </nav>

        {loginUserData?.vendor_id && vendorIsApproved && (
          <button className="relative flex items-center px-1 py-2 rounded-full bg-gray-100/80 hover:bg-gray-100 transition-all duration-300 mr-8">
            <div
              className={`absolute h-7 w-[90px] top-1 transition-all duration-500 ease-in-out ${
                userRole === "vendor" ? "left-2" : "left-[75px]"
              }`}
            >
              <div className="h-full w-[67px] rounded-full bg-white shadow-[0_2px_10px_-3px_rgba(0,0,0,0.1)] border border-gray-100" />
            </div>
            <div className="relative z-10 flex items-center">
              <span
                onClick={() => {
                  localStorage.setItem("userRole", "vendor");
                  setUserRole("vendor");
                  navigate("/vendor/dashboard");
                }}
                className={`px-4 text-sm font-medium transition-colors duration-300 ${
                  userRole === "vendor"
                    ? "text-orange-600"
                    : "text-gray-400 hover:text-gray-500"
                }`}
              >
                Vendor
              </span>
              <span
                onClick={() => {
                  localStorage.setItem("userRole", "user");
                  setUserRole("user");
                }}
                className={`px-4 text-sm font-medium transition-colors duration-300 ${
                  userRole === "user"
                    ? "text-orange-600"
                    : "text-gray-400 hover:text-gray-500"
                }`}
              >
                User
              </span>
            </div>
          </button>
        )}

        <div className="flex items-center space-x-4 ml-auto">
          <NotificationBell />

          <div
            className="relative cursor-pointer text-gray-600 hover:text-black flex items-center user-dropdown-container"
            onClick={handleUserDropdownToggle}
            ref={userDropdownRef}
          >
            <UserCircleIcon className="h-7 w-7" />
            <ChevronDownIcon className="h-4 w-4 ml-1 text-gray-400" />
            <div
              className={`absolute top-full right-0 mt-[21px] w-48 bg-white border border-gray-200 rounded-md shadow-sm z-50 ${
                isUserDropdownOpen ? "block" : "hidden"
              }`}
            >
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  navigate("/UserProfile");
                  setIsUserDropdownOpen(false);
                }}
                className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
              >
                User Profile
              </button>

              <button
                onClick={(e) => {
                  e.stopPropagation();
                  navigate("/mypurchasedeal");
                  setIsUserDropdownOpen(false);
                }}
                className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
              >
                My Orders
              </button>

              {!vendorId && (
                <button
                  onClick={handleBecomeAVendorClick}
                  className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                >
                  Become a vendor
                </button>
              )}

              {vendorId && !vendorIsApproved && (
                <button
                  onClick={handleBecomeAVendorClick}
                  className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                >
                  Become a vendor
                  <p className="text-yellow-600 text-xs font-semibold">
                    Pending
                  </p>
                </button>
              )}

              {vendorId && vendorIsApproved && (
                <button
                  onClick={handleBecomeAVendorClick}
                  className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                >
                  Become a vendor
                  <p className="text-green-600 text-xs font-semibold">
                    Approved <span className="text-orange-500">(ReApply)</span>
                  </p>
                </button>
              )}

              <button
                onClick={(e) => {
                  e.stopPropagation();
                  navigate("/Settings");
                  setIsUserDropdownOpen(false);
                }}
                className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
              >
                Settings
              </button>

              <button
                onClick={(e) => {
                  e.stopPropagation();
                  navigate("/ContactSupport");
                  setIsUserDropdownOpen(false);
                }}
                className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
              >
                Help & Support
              </button>

              <button
                onClick={handleLogoutClick}
                className="block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};

export default UserHeader;
